import { useEffect, useMemo, useState } from 'react';
import '../styles/joke-toast.css';
import { formatDurationShort } from '../lib/format.js';

const OUT_MS = 250;
const MAX_TASKS = 6;

function Kaboom() {
  return (
    <svg className="joke-kaboom" viewBox="0 0 64 64" aria-hidden="true">
      <polygon
        points="32,2 38,22 60,18 44,34 60,50 38,46 32,62 26,46 4,50 20,34 4,18 26,22"
        fill="#ffd84d"
        stroke="#0d0d12"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function WelcomeCard({ employee, stats, onDismiss }) {
  const [closing, setClosing] = useState(false);

  const { firstName, yesterdayTasks, yesterdayTotal, yesterdayLabel } = useMemo(() => {
    const name = (employee?.name || '').trim().split(/\s+/)[0] || 'there';
    // Use OWNED totals — sum of time on tasks they own, including work
    // logged by teammates. Matches what the Airtable rollup shows in
    // their personal view. The `personal` totals would only show what
    // they personally tracked, which understates supervisor workload.
    const days = stats?.owned?.days || stats?.personal?.days || [];
    const yesterday = days.length >= 2 ? days[days.length - 2] : null;
    const yesterdayDate = yesterday?.date || null;
    const tasks = yesterdayDate ? (stats?.weekByDay?.[yesterdayDate] || []) : [];
    const total = yesterday?.seconds || 0;

    let label = 'Yesterday';
    if (yesterdayDate) {
      try {
        const d = new Date(yesterdayDate + 'T00:00:00');
        label = d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
      } catch {}
    }

    return {
      firstName: name,
      yesterdayTasks: tasks.slice().sort((a, b) => b.seconds - a.seconds),
      yesterdayTotal: total,
      yesterdayLabel: label
    };
  }, [employee, stats]);

  // Esc + click outside to dismiss.
  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') {
        setClosing(true);
        setTimeout(onDismiss, OUT_MS);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  const handleBackdropClick = () => {
    setClosing(true);
    setTimeout(onDismiss, OUT_MS);
  };

  const visibleTasks = yesterdayTasks.slice(0, MAX_TASKS);
  const moreCount = Math.max(0, yesterdayTasks.length - visibleTasks.length);

  return (
    <div
      className="joke-root"
      onClick={handleBackdropClick}
      style={closing ? { opacity: 0, transition: `opacity ${OUT_MS}ms ease` } : undefined}
    >
      <div className="joke-card v-welcome in" onClick={e => e.stopPropagation()}>
        <div className="joke-speedlines" />
        <div className="joke-halftone" />
        <Kaboom />
        <button
          type="button"
          className="joke-close"
          onClick={e => { e.stopPropagation(); handleBackdropClick(); }}
          aria-label="Close welcome"
        >×</button>
        <div className="joke-content">
          <h2 className="joke-headline">
            <span>Salaam, </span>
            <em>{firstName}</em>
            <span>!</span>
          </h2>
          <div className="joke-welcome-sub">{yesterdayLabel} · the run-down</div>
          {visibleTasks.length === 0 ? (
            <div className="joke-welcome-empty">No tasks tracked yesterday — fresh slate today.</div>
          ) : (
            <ul className="joke-welcome-list">
              {visibleTasks.map(t => (
                <li key={t.taskId}>
                  <span className="name" title={t.name}>{t.name}</span>
                  <span className="dur">{formatDurationShort(t.seconds)}</span>
                </li>
              ))}
              {moreCount > 0 && (
                <li>
                  <span className="name" style={{ color: 'var(--jt-ink-2)', fontStyle: 'italic' }}>
                    + {moreCount} more
                  </span>
                  <span className="dur">—</span>
                </li>
              )}
            </ul>
          )}
          <div className="joke-welcome-total">
            <span className="label">Total</span>
            <span className="value">{formatDurationShort(yesterdayTotal)}</span>
          </div>
          <div className="joke-welcome-tagline">
            Tap anywhere or press <kbd>Esc</kbd> to dismiss.
          </div>
        </div>
      </div>
    </div>
  );
}
